/**
 * Persisted display preferences for the mobile Products screen — group by, sort,
 * and which row-detail fields show. Scoped per store in localStorage so the
 * choice survives reloads, mirroring useOrdersDisplayPrefs.
 */

import { useCallback, useEffect, useState } from "react";
import type { ProductsGroupBy, ProductsSort, RowProp } from "./products-mobile-model";

export type RowProps = Record<RowProp, boolean>;

export type ProductsDisplayPrefs = {
  group: ProductsGroupBy;
  sort: ProductsSort;
  props: RowProps;
};

const DEFAULTS: ProductsDisplayPrefs = {
  group: "category",
  sort: "recent",
  props: {
    category: true,
    stock: true,
  },
};

const VERSION = 1;
const key = (storeSlug: string) => `seller:products-display:v${VERSION}:${storeSlug}`;

function read(storeSlug: string): ProductsDisplayPrefs {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(key(storeSlug));
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<ProductsDisplayPrefs>;
    return {
      group: parsed.group ?? DEFAULTS.group,
      sort: parsed.sort ?? DEFAULTS.sort,
      props: { ...DEFAULTS.props, ...(parsed.props ?? {}) },
    };
  } catch {
    return DEFAULTS;
  }
}

export function useProductsDisplayPrefs(storeSlug: string) {
  const [prefs, setPrefs] = useState<ProductsDisplayPrefs>(() => read(storeSlug));

  useEffect(() => {
    setPrefs(read(storeSlug));
  }, [storeSlug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key(storeSlug), JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs, storeSlug]);

  const setGroup = useCallback((group: ProductsGroupBy) => setPrefs((p) => ({ ...p, group })), []);
  const setSort = useCallback((sort: ProductsSort) => setPrefs((p) => ({ ...p, sort })), []);
  const toggleProp = useCallback(
    (prop: RowProp) => setPrefs((p) => ({ ...p, props: { ...p.props, [prop]: !p.props[prop] } })),
    [],
  );
  const resetProps = useCallback(() => setPrefs((p) => ({ ...p, props: { ...DEFAULTS.props } })), []);

  return { prefs, setGroup, setSort, toggleProp, resetProps };
}
